import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Car, MapPin, Calendar, Clock, Users, Luggage, Plane, ArrowRight,
  Star, Shield, Check, ChevronDown, Phone, Mail, AlertCircle,
  Package, Utensils, FileText, Timer, CarTaxiFront, Award,
  Plus, Minus, Info, Loader2, Search, TruckIcon, Briefcase, Crown
} from 'lucide-react';
import { ar, de, enUS, es, faIR, fr, it, pt, ru, tr, zhCN } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar as DayCalendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageSeo } from '@/components/seo/PageSeo';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { taxiBooking, taxiCarrier } from '@/lib/api';
import { buildPathForLocale } from '@/lib/intl-seo';

// Vehicle class icons
const VEHICLE_CLASS_ICONS = {
  economy: Car,
  vip: Crown,
  komfort: Car,
  suv: TruckIcon,
  business: Briefcase,
  van: TruckIcon,
  premium: Award,
  limousine: Crown,
  kleinbus: TruckIcon,
  autobus: TruckIcon,
};

// Default vehicle class images (from Unsplash - free to use)
const VEHICLE_CLASS_IMAGES = {
  economy: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=300&h=200&fit=crop',
  komfort: 'https://images.unsplash.com/photo-1550355291-bbee04a92027?w=300&h=200&fit=crop',
  business: 'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?w=300&h=200&fit=crop',
  premium: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=300&h=200&fit=crop',
  limousine: 'https://images.unsplash.com/photo-1563720223185-11003d516935?w=300&h=200&fit=crop',
  vip: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=300&h=200&fit=crop',
  suv: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=300&h=200&fit=crop',
  van: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=300&h=200&fit=crop',
  kleinbus: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=300&h=200&fit=crop',
  autobus: 'https://images.unsplash.com/photo-1557223562-6c77ef16210f?w=300&h=200&fit=crop',
};

const DATE_LOCALES = { tr, en: enUS, de, fr, es, it, pt, ru, zh: zhCN, ar, fa: faIR };

const TIME_SLOTS = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

const pad2 = (n) => String(n).padStart(2, '0');

const toLocalDateTimeValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const parseYmd = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

function DateTimeField({ value, onChange, min, locale, datePlaceholder, timePlaceholder }) {
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const datePart = value?.slice(0, 10) || '';
  const timePart = value && value.length >= 16 ? value.slice(11, 16) : '';
  const minDate = min?.slice(0, 10) || '';
  const minTime = min && min.length >= 16 ? min.slice(11, 16) : '';
  const selected = datePart ? parseYmd(datePart) : undefined;
  const lang = (locale || 'tr').split('-')[0];
  const dateLocale = DATE_LOCALES[lang] || enUS;

  const commit = (nextDate, nextTime) => {
    if (nextDate && nextTime) onChange(`${nextDate}T${nextTime}`);
    else onChange(nextDate || '');
  };

  const timeAllowed = (slot) => !minDate || datePart !== minDate || !minTime || slot >= minTime;

  return (
    <div className="flex gap-2">
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="h-12 flex-1 justify-start font-normal px-3">
            <Calendar className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className={datePart ? '' : 'text-muted-foreground'}>
              {datePart
                ? parseYmd(datePart).toLocaleDateString(locale || 'tr', { day: '2-digit', month: 'short', year: 'numeric' })
                : datePlaceholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <DayCalendar
            mode="single"
            selected={selected}
            locale={dateLocale}
            weekStartsOn={1}
            disabled={minDate ? { before: parseYmd(minDate) } : undefined}
            onSelect={(day) => {
              if (!day) return;
              const ymd = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
              const nextTime = timePart && (ymd !== minDate || !minTime || timePart >= minTime) ? timePart : '';
              commit(ymd, nextTime);
              setDateOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      <Popover open={timeOpen} onOpenChange={setTimeOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="h-12 w-[108px] justify-start font-normal px-3" disabled={!datePart}>
            <Clock className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className={timePart ? '' : 'text-muted-foreground'}>{timePart || timePlaceholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2" align="end">
          <div className="grid grid-cols-3 gap-1 max-h-56 overflow-y-auto">
            {TIME_SLOTS.map((slot) => (
              <Button
                key={slot}
                type="button"
                variant={timePart === slot ? 'default' : 'ghost'}
                className="h-8 px-0 text-xs"
                disabled={!timeAllowed(slot)}
                onClick={() => {
                  commit(datePart, slot);
                  setTimeOpen(false);
                }}
              >
                {slot}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function AddressField({ value, placeholder, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    const query = (value || '').trim();
    if (query.length < 2) {
      setItems([]);
      return undefined;
    }
    let cancel = false;
    const timer = setTimeout(async () => {
      try {
        const res = await taxiBooking.placeSuggestions(query);
        if (!cancel) setItems(res.data?.suggestions || []);
      } catch {
        if (!cancel) setItems([]);
      }
    }, 280);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [value]);

  useEffect(() => {
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setOpen(true);
          onPick({ label: e.target.value, lat: null, lng: null });
        }}
        onFocus={() => setOpen(true)}
        className="h-12"
        data-testid={testId}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white py-1 shadow-lg">
          {items.map((item) => (
            <li key={`${item.kind}-${item.label}`}>
              <button
                type="button"
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-orange-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(item);
                  setOpen(false);
                }}
              >
                <span className="text-sm font-medium text-gray-900">{item.label}</span>
                {item.subtitle ? <span className="text-xs text-muted-foreground">{item.subtitle}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const TaxiLimousinePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language?.split('-')[0] || 'tr';
  
  // Booking type tabs
  const [bookingType, setBookingType] = useState('transfer');
  
  // Vehicle classes
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    // Transfer
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    dropoff_address: '',
    dropoff_lat: null,
    dropoff_lng: null,
    pickup_datetime: '',
    return_trip: false,
    return_datetime: '',
    // Passengers
    passengers: 1,
    luggage: 0,
    children: { '0-3': 0, '3-6': 0, '6-12': 0 },
    // Hourly
    duration_hours: 1,
    // Delivery
    delivery_type: 'documents',
    // Flight
    flight_number: '',
    // Contact
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    comments: '',
    promo_code: ''
  });
  
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingRequest, setBookingRequest] = useState(null);

  // Load vehicle classes and popular routes
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [classesRes, routesRes] = await Promise.all([
        taxiCarrier.getVehicleClasses(),
        taxiBooking.getPopularRoutes()
      ]);
      setVehicleClasses(classesRes.data.vehicle_classes || []);
      setPopularRoutes(routesRes.data.popular_routes || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handlePassengerChange = (delta) => {
    const newValue = Math.max(1, Math.min(50, bookingForm.passengers + delta));
    setBookingForm({ ...bookingForm, passengers: newValue });
  };

  const handleLuggageChange = (delta) => {
    const newValue = Math.max(0, Math.min(50, bookingForm.luggage + delta));
    setBookingForm({ ...bookingForm, luggage: newValue });
  };

  const handleChildrenChange = (ageGroup, delta) => {
    const newValue = Math.max(0, Math.min(10, (bookingForm.children[ageGroup] || 0) + delta));
    setBookingForm({
      ...bookingForm,
      children: { ...bookingForm.children, [ageGroup]: newValue }
    });
  };

  const handleDurationChange = (hours) => {
    setBookingForm({ ...bookingForm, duration_hours: hours });
  };

  const handleSubmitBooking = async () => {
    try {
      // Validation
      if (!bookingForm.pickup_address) {
        toast.error(t('taxi.enterPickupAddress', 'Please enter pickup address'));
        return;
      }
      if (bookingType === 'transfer' && !bookingForm.dropoff_address) {
        toast.error(t('taxi.enterDropoffAddress', 'Please enter drop-off address'));
        return;
      }
      if (!bookingForm.pickup_datetime || bookingForm.pickup_datetime.length < 16) {
        toast.error(t('taxi.selectDateTime', 'Please select date and time'));
        return;
      }
      if (bookingForm.return_trip && (!bookingForm.return_datetime || bookingForm.return_datetime.length < 16)) {
        toast.error(t('taxi.selectDateTime', 'Please select date and time'));
        return;
      }
      if (!bookingForm.contact_name || !bookingForm.contact_phone || !bookingForm.contact_email) {
        toast.error(t('taxi.enterContactInfo', 'Please enter contact information'));
        return;
      }

      setLoading(true);
      
      const requestData = {
        booking_type: bookingType,
        pickup_address: bookingForm.pickup_address,
        pickup_lat: bookingForm.pickup_lat,
        pickup_lng: bookingForm.pickup_lng,
        pickup_datetime: new Date(bookingForm.pickup_datetime).toISOString(),
        dropoff_address: bookingType !== 'hourly' ? bookingForm.dropoff_address : null,
        dropoff_lat: bookingType !== 'hourly' ? bookingForm.dropoff_lat : null,
        dropoff_lng: bookingType !== 'hourly' ? bookingForm.dropoff_lng : null,
        duration_hours: bookingType === 'hourly' ? bookingForm.duration_hours : null,
        passengers: bookingForm.passengers,
        luggage: bookingForm.luggage,
        children: bookingForm.children,
        preferred_vehicle_class: selectedClass,
        flight_number: bookingForm.flight_number || null,
        return_trip: bookingForm.return_trip,
        return_datetime: bookingForm.return_trip ? bookingForm.return_datetime : null,
        comments: bookingForm.comments || null,
        delivery_type: bookingType === 'delivery' ? bookingForm.delivery_type : null,
        contact_name: bookingForm.contact_name,
        contact_phone: bookingForm.contact_phone,
        contact_email: bookingForm.contact_email,
        language: currentLang || 'tr',
        promo_code: bookingForm.promo_code || null
      };

      const res = await taxiBooking.createRequest(requestData);
      setBookingRequest(res.data.booking_request);
      setShowBookingDialog(true);
      toast.success(t('taxi.requestCreated', 'Booking request created successfully!'));
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Duration options for hourly booking
  const durationOptions = [
    { value: 0.5, label: '30 ' + t('taxi.minutes', 'dk') },
    { value: 1, label: '1 ' + t('taxi.hour', 'hour') },
    { value: 2, label: '2 ' + t('taxi.hours', 'hours') },
    { value: 3, label: '3 ' + t('taxi.hours', 'hours') },
    { value: 4, label: '4 ' + t('taxi.hours', 'hours') },
    { value: 6, label: '6 ' + t('taxi.hours', 'hours') },
    { value: 8, label: '8 ' + t('taxi.hours', 'hours') },
  ];

  // Delivery types
  const deliveryTypes = [
    { value: 'documents', label: t('taxi.documents', 'Documents'), icon: FileText },
    { value: 'box_10kg', label: t('taxi.box10kg', 'Box (up to 10kg)'), icon: Package },
    { value: 'freight', label: t('taxi.freight', 'Freight'), icon: TruckIcon },
    { value: 'food', label: t('taxi.food', 'Food Delivery'), icon: Utensils },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="taxi-limousine-page">
      <PageSeo title={t('taxi.heroTitle', 'Premium Transfer & Limousine Service')} description={t('taxi.heroSubtitle', 'Book reliable airport transfers, city rides, and hourly rentals')} segments={['taxi-limousine']} />
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 mb-5">
              <Button
                type="button"
                size="lg"
                variant="outline"
                className="h-auto min-h-12 px-5 py-3 text-base font-semibold bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                onClick={() => navigate(buildPathForLocale(currentLang, ['car-rentals']))}
              >
                <Car className="w-5 h-5 mr-2 shrink-0" />
                {t('carRental.heroTitle', 'Hayalinizdeki Aracı Kiralayın')}
              </Button>
              <Button
                type="button"
                size="lg"
                className="h-auto min-h-12 px-5 py-3 text-base font-semibold bg-amber-500 hover:bg-amber-400 text-black hover:text-black"
              >
                <Crown className="w-5 h-5 mr-2 shrink-0" />
                {t('carRental.heroLimousineTitle', 'Hayalinizdeki Limuzini Kiralayın')}
              </Button>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="taxi-hero-title">
              {t('taxi.heroTitle', 'Premium Transfer & Limousine Service')}
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              {t('taxi.heroSubtitle', 'Book reliable airport transfers, city rides, and hourly rentals')}
            </p>
          </div>

          {/* Booking Form Card */}
          <Card className="max-w-4xl mx-auto bg-white text-gray-900 shadow-2xl" data-testid="booking-form-card">
            <CardContent className="p-6">
              {/* Booking Type Tabs */}
              <Tabs value={bookingType} onValueChange={setBookingType} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6" data-testid="booking-type-tabs">
                  <TabsTrigger value="transfer" className="gap-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="transfer-tab">
                    <Car className="w-4 h-4" />
                    {t('taxi.transfer', 'Transfer')}
                  </TabsTrigger>
                  <TabsTrigger value="hourly" className="gap-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="hourly-tab">
                    <Timer className="w-4 h-4" />
                    {t('taxi.hourlyRate', 'Hourly Rate')}
                  </TabsTrigger>
                  <TabsTrigger value="delivery" className="gap-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="delivery-tab">
                    <Package className="w-4 h-4" />
                    {t('taxi.delivery', 'Delivery')}
                  </TabsTrigger>
                </TabsList>

                {/* Transfer Tab */}
                <TabsContent value="transfer" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-green-600" />
                        {t('taxi.pickupAddress', 'Pickup Address')}
                      </Label>
                      <AddressField
                        placeholder={t('taxi.enterAddress', 'Enter address, airport, or hotel...')}
                        value={bookingForm.pickup_address}
                        onPick={(item) => setBookingForm({
                          ...bookingForm,
                          pickup_address: item.label,
                          pickup_lat: item.lat ?? null,
                          pickup_lng: item.lng ?? null,
                        })}
                        testId="pickup-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-600" />
                        {t('taxi.dropoffAddress', 'Drop-off Address')}
                      </Label>
                      <AddressField
                        placeholder={t('taxi.enterAddress')}
                        value={bookingForm.dropoff_address}
                        onPick={(item) => setBookingForm({
                          ...bookingForm,
                          dropoff_address: item.label,
                          dropoff_lat: item.lat ?? null,
                          dropoff_lng: item.lng ?? null,
                        })}
                        testId="dropoff-address-input"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Hourly Tab */}
                <TabsContent value="hourly" className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-600" />
                      {t('taxi.startAddress', 'Starting Address')}
                    </Label>
                    <AddressField
                      placeholder={t('taxi.enterAddress')}
                      value={bookingForm.pickup_address}
                      onPick={(item) => setBookingForm({
                        ...bookingForm,
                        pickup_address: item.label,
                        pickup_lat: item.lat ?? null,
                        pickup_lng: item.lng ?? null,
                      })}
                      testId="hourly-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Timer className="w-4 h-4" />
                      {t('taxi.duration', 'Duration')}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {durationOptions.map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={bookingForm.duration_hours === opt.value ? 'default' : 'outline'}
                          className={bookingForm.duration_hours === opt.value ? 'bg-orange-500 hover:bg-orange-600' : ''}
                          onClick={() => handleDurationChange(opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Delivery Tab */}
                <TabsContent value="delivery" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-green-600" />
                        {t('taxi.pickupAddress')}
                      </Label>
                      <AddressField
                        placeholder={t('taxi.enterAddress')}
                        value={bookingForm.pickup_address}
                        onPick={(item) => setBookingForm({
                          ...bookingForm,
                          pickup_address: item.label,
                          pickup_lat: item.lat ?? null,
                          pickup_lng: item.lng ?? null,
                        })}
                        testId="delivery-pickup-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-600" />
                        {t('taxi.deliveryAddress', 'Delivery Address')}
                      </Label>
                      <AddressField
                        placeholder={t('taxi.enterAddress')}
                        value={bookingForm.dropoff_address}
                        onPick={(item) => setBookingForm({
                          ...bookingForm,
                          dropoff_address: item.label,
                          dropoff_lat: item.lat ?? null,
                          dropoff_lng: item.lng ?? null,
                        })}
                        testId="delivery-dropoff-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('taxi.deliveryType', 'Delivery Type')}</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {deliveryTypes.map((type) => {
                        const TypeIcon = type.icon;
                        return (
                          <Button
                            key={type.value}
                            type="button"
                            variant={bookingForm.delivery_type === type.value ? 'default' : 'outline'}
                            className={`flex items-center gap-2 ${bookingForm.delivery_type === type.value ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                            onClick={() => setBookingForm({ ...bookingForm, delivery_type: type.value })}
                          >
                            <TypeIcon className="w-4 h-4" />
                            {type.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                {/* Common Fields */}
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  {/* Date Time */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {t('taxi.dateTime', 'Date & Time')}
                    </Label>
                    <DateTimeField
                      value={bookingForm.pickup_datetime}
                      onChange={(pickup_datetime) => setBookingForm({ ...bookingForm, pickup_datetime })}
                      min={toLocalDateTimeValue(new Date())}
                      locale={i18n.language}
                      datePlaceholder={t('taxi.pickDate', 'Tarih')}
                      timePlaceholder={t('taxi.pickTime', 'Saat')}
                    />
                  </div>

                  {/* Passengers */}
                  {bookingType !== 'delivery' && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {t('taxi.passengers', 'Passengers')}
                      </Label>
                      <div className="flex items-center h-12 border rounded-md">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full px-3"
                          onClick={() => handlePassengerChange(-1)}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="flex-1 text-center font-medium">{bookingForm.passengers}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full px-3"
                          onClick={() => handlePassengerChange(1)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Luggage */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Luggage className="w-4 h-4" />
                      {t('taxi.luggage', 'Luggage')}
                    </Label>
                    <div className="flex items-center h-12 border rounded-md">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full px-3"
                        onClick={() => handleLuggageChange(-1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="flex-1 text-center font-medium">{bookingForm.luggage}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full px-3"
                        onClick={() => handleLuggageChange(1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Extra Options */}
                {bookingType === 'transfer' && (
                  <div className="flex flex-wrap gap-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="return_trip"
                        checked={bookingForm.return_trip}
                        onCheckedChange={(checked) => setBookingForm({ ...bookingForm, return_trip: checked })}
                      />
                      <label htmlFor="return_trip" className="text-sm cursor-pointer">
                        {t('taxi.returnTrip', 'Return trip')}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-gray-500" />
                      <Input
                        placeholder={t('taxi.flightNumber', 'Flight number (optional)')}
                        value={bookingForm.flight_number}
                        onChange={(e) => setBookingForm({ ...bookingForm, flight_number: e.target.value })}
                        className="w-40"
                      />
                    </div>
                  </div>
                )}

                {/* Return DateTime */}
                {bookingForm.return_trip && (
                  <div className="mt-4">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {t('taxi.returnDateTime', 'Return Date & Time')}
                    </Label>
                    <div className="max-w-sm mt-2">
                      <DateTimeField
                        value={bookingForm.return_datetime}
                        onChange={(return_datetime) => setBookingForm({ ...bookingForm, return_datetime })}
                        min={bookingForm.pickup_datetime || toLocalDateTimeValue(new Date())}
                        locale={i18n.language}
                        datePlaceholder={t('taxi.pickDate', 'Tarih')}
                        timePlaceholder={t('taxi.pickTime', 'Saat')}
                      />
                    </div>
                  </div>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Vehicle Classes */}
      <section className="py-12 bg-white" data-testid="vehicle-classes-section">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">
            {t('taxi.selectVehicleClass', 'Select Vehicle Class')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto" data-testid="vehicle-classes-grid">
            {vehicleClasses.map((vc) => {
              const VcIcon = VEHICLE_CLASS_ICONS[vc.id] || Car;
              const vehicleImage = VEHICLE_CLASS_IMAGES[vc.id];
              const isSelected = selectedClass === vc.id;
              
              return (
                <Card
                  key={vc.id}
                  className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 ${
                    isSelected ? 'ring-2 ring-orange-500 shadow-lg' : ''
                  }`}
                  onClick={() => setSelectedClass(isSelected ? null : vc.id)}
                  data-testid={`vehicle-class-${vc.id}`}
                >
                  <CardContent className="p-0">
                    {/* Vehicle Image */}
                    <div className="relative h-28 overflow-hidden rounded-t-lg bg-gray-100">
                      {vehicleImage ? (
                        <img 
                          src={vehicleImage} 
                          alt={vc.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <VcIcon className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-orange-500 text-white rounded-full p-1">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    {/* Vehicle Info */}
                    <div className="p-3 text-center">
                      <p className={`font-semibold ${isSelected ? 'text-orange-600' : 'text-gray-800'}`}>
                        {t(`taxi.${vc.id === 'komfort' ? 'comfort' : vc.id}`, vc.name_tr || vc.name)}
                      </p>
                      <div className="flex items-center justify-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {vc.max_passengers}
                        </span>
                        <span className="flex items-center gap-1">
                          <Luggage className="w-3 h-3" />
                          {vc.max_luggage || 2}
                        </span>
                      </div>
                      {vc.base_price && (
                        <p className="text-orange-600 font-semibold mt-2">
                          €{vc.base_price}+
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>{t('taxi.contactInfo', 'Contact Information')}</CardTitle>
                <CardDescription>{t('taxi.contactInfoDesc', 'We need this to send you offers and booking confirmation')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('taxi.name', 'Ad Soyad')} *</Label>
                    <Input
                      value={bookingForm.contact_name}
                      onChange={(e) => setBookingForm({ ...bookingForm, contact_name: e.target.value })}
                      placeholder={t('taxi.yourName', 'Your full name')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('taxi.phone', 'Telefon')} *</Label>
                    <Input
                      type="tel"
                      value={bookingForm.contact_phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, contact_phone: e.target.value })}
                      placeholder={t('taxi.phonePlaceholder', '+90 555 000 00 00')}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('taxi.email', 'E-posta')} *</Label>
                  <Input
                    type="email"
                    value={bookingForm.contact_email}
                    onChange={(e) => setBookingForm({ ...bookingForm, contact_email: e.target.value })}
                    placeholder={t('taxi.emailPlaceholder', 'ornek@email.com')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('taxi.comments', 'Comments')}</Label>
                  <Textarea
                    value={bookingForm.comments}
                    onChange={(e) => setBookingForm({ ...bookingForm, comments: e.target.value })}
                    placeholder={t('taxi.commentsPlaceholder', 'Special requests, child seats, meeting point details...')}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('taxi.promoCode', 'Promo Code')}</Label>
                  <Input
                    value={bookingForm.promo_code}
                    onChange={(e) => setBookingForm({ ...bookingForm, promo_code: e.target.value })}
                    placeholder={t('taxi.enterPromoCode', 'Enter promo code (optional)')}
                    className="max-w-xs"
                  />
                </div>

                <Button
                  onClick={handleSubmitBooking}
                  disabled={loading}
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-lg"
                  data-testid="get-offers-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Search className="w-5 h-5 mr-2" />
                  )}
                  {t('taxi.getOffers', 'Get Offers')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      {popularRoutes.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8">
              {t('taxi.popularRoutes', 'Popular Routes')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {popularRoutes.map((route, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setBookingForm({
                      ...bookingForm,
                      pickup_address: route.from,
                      pickup_lat: null,
                      pickup_lng: null,
                      dropoff_address: route.to,
                      dropoff_lat: null,
                      dropoff_lng: null,
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-3 h-3 text-green-600" />
                          <span className="truncate">{route.from}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3 text-red-600" />
                          <span className="truncate">{route.to}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{route.distance_km} km</p>
                        <p className="font-semibold text-orange-600">
                          €{route.estimated_price.min}-{route.estimated_price.max}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-12">
            {t('taxi.whyChooseUs', 'Why Choose Us')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{t('taxi.verifiedDrivers', 'Verified Drivers')}</h3>
              <p className="text-gray-400 text-sm">{t('taxi.verifiedDriversDesc', 'All carriers are verified with valid licenses')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{t('taxi.topRated', 'Top Rated')}</h3>
              <p className="text-gray-400 text-sm">{t('taxi.topRatedDesc', 'Choose from carriers with excellent ratings')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{t('taxi.24Support', '24/7 Support')}</h3>
              <p className="text-gray-400 text-sm">{t('taxi.24SupportDesc', 'Support available around the clock')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{t('taxi.freeCancellation', 'Free Cancellation')}</h3>
              <p className="text-gray-400 text-sm">{t('taxi.freeCancellationDesc', 'Cancel free up to 48 hours before')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Become a Carrier CTA */}
      <section className="py-12 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">
            {t('taxi.becomeCarrier', 'Become a Carrier')}
          </h2>
          <p className="mb-6 max-w-xl mx-auto">
            {t('taxi.carrierCTA', 'Do you have a vehicle? Join our network and start earning money with your car.')}
          </p>
          <Link to={`/${currentLang}/seller-dashboard`}>
            <Button variant="secondary" size="lg" className="gap-2">
              <CarTaxiFront className="w-5 h-5" />
              {t('taxi.registerAsCarrier', 'Register as Carrier')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Booking Success Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-6 h-6" />
              {t('taxi.requestCreated', 'Request Created!')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-lg font-medium">{t('taxi.requestNumber', 'Request Number')}</p>
              <p className="text-2xl font-mono font-bold text-green-700">
                {bookingRequest?.request_number}
              </p>
            </div>
            <div className="text-center text-muted-foreground">
              <p>{t('taxi.waitingForOffers', 'Your request has been sent to carriers.')}</p>
              <p>{t('taxi.offersWillArrive', 'You will receive offers via email shortly.')}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                {t('taxi.checkEmailTip', 'Check your email and spam folder for carrier offers.')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBookingDialog(false)} className="w-full bg-orange-500 hover:bg-orange-600">
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxiLimousinePage;
